/* Botanical avatar glyphs for the account-lite profile. */

import type { ReactElement } from 'react';
import type { AvatarId } from '../state/ProfileContext';

interface AvatarDef {
  bg: string;
  fg: string;
  glyph: ReactElement;
}

const A: Record<AvatarId, AvatarDef> = {
  sprout: {
    bg: 'var(--vine-wash)',
    fg: 'var(--vine)',
    glyph: (
      <>
        <path d="M16 24V13" />
        <path d="M16 16c-4 0-6-2-6-6 4 0 6 2 6 6Z" />
        <path d="M16 14c4 0 6-2 6-6-4 0-6 2-6 6Z" />
      </>
    ),
  },
  leaf: {
    bg: 'var(--vine-wash)',
    fg: 'var(--vine-bright)',
    glyph: (
      <>
        <path d="M9 23c0-8 6-14 14-14 0 8-6 14-14 14Z" />
        <path d="M9 23c4-4 8-8 12-10" />
      </>
    ),
  },
  fern: {
    bg: 'var(--vine-wash)',
    fg: 'var(--vine-deep)',
    glyph: (
      <>
        <path d="M16 24c0-8 0-14 0-18" />
        <path d="M16 10c2-1 4-1 6-3M16 14c2-1 4-1 6-3M16 18c1.6-1 3-1 5-2.6M16 10c-2-1-4-1-6-3M16 14c-2-1-4-1-6-3M16 18c-1.6-1-3-1-5-2.6" />
      </>
    ),
  },
  bud: {
    bg: 'var(--berry-wash)',
    fg: 'var(--berry)',
    glyph: (
      <>
        <path d="M16 24v-7" />
        <circle cx="16" cy="12" r="5" />
        <path d="M11 12c0-3 2-5 5-5s5 2 5 5" />
      </>
    ),
  },
  clover: {
    bg: 'var(--vine-wash)',
    fg: 'var(--vine)',
    glyph: (
      <>
        <circle cx="12" cy="13" r="3.4" />
        <circle cx="20" cy="13" r="3.4" />
        <circle cx="16" cy="9" r="3.4" />
        <path d="M16 13v11" />
      </>
    ),
  },
  flower: {
    bg: 'var(--seed-wash)',
    fg: 'var(--seed)',
    glyph: (
      <>
        <circle cx="16" cy="13" r="2.4" />
        <path d="M16 6a3.4 3.4 0 0 1 0 6.8M16 6a3.4 3.4 0 0 0 0 6.8M9.7 9.5a3.4 3.4 0 0 1 5.9 3.4M22.3 9.5a3.4 3.4 0 0 0-5.9 3.4" />
        <path d="M16 15v9" />
      </>
    ),
  },
  seed: {
    bg: 'var(--seed-wash)',
    fg: 'var(--seed)',
    glyph: (
      <>
        <path d="M16 7c4 3 4 11 0 18-4-7-4-15 0-18Z" />
        <path d="M16 11v9" />
      </>
    ),
  },
  palm: {
    bg: 'var(--vine-wash)',
    fg: 'var(--vine-bright)',
    glyph: (
      <>
        <path d="M16 24c0-7-1-11-1-13" />
        <path d="M15 11c-3-2-6-2-9 0M15 11c3-2 6-2 9 0M15 11c-1-3-3-5-6-5M15 11c1-3 3-5 6-5" />
      </>
    ),
  },
};

interface AvatarProps {
  id: AvatarId;
  size?: number;
}

export function Avatar({ id, size = 40 }: AvatarProps) {
  const def = A[id] ?? A.sprout;
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" role="img" aria-label={`${id} avatar`}>
      <circle cx="16" cy="16" r="16" fill={def.bg} />
      <g
        fill="none"
        stroke={def.fg}
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {def.glyph}
      </g>
    </svg>
  );
}
